import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Button, Loading, Modal, Text, Tooltip } from "@nextui-org/react";
import { Filter2, PaperDownload } from "react-iconly";

import { secureFetch } from "../../lib/api";
import endpoint from "../../config/endpoint.json";
import Filter from "../../components/SafetyDocumentComponent/Filter";
import { error, success } from "../../lib/userAlerts";
import checkAccess from '../../lib/checkAccess';
import SubPageHeader from "../../components/SubPageHeader";
import { mstore } from "../../store";

const COLUMN_DEFS = [
  { headerName: "Document Number", field: "id", width: 200 },
  { headerName: "Date Entered", field: "uploaded_time", width: 200 },
  { headerName: "Employee Id", field: "employee_id", width: 200 },
  { headerName: "Document Type", field: "document_type", width: 200 },
  { headerName: "Status", field: "status", width: 200 }
];

const DocumentPreview = ({ document, onClose, onStatusUpdate, isUpdating, onDownload }) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const loadPreview = async () => {
      setPdfLoading(true);
      try {
        const response = await fetch(document.docUrl);
        const blob = await response.blob();
        const file = new Blob([blob], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        setPreviewUrl(fileURL);
      } catch (err) {
        error("Failed to load document preview");
      } finally {
        setPdfLoading(false);
      }
    };

    loadPreview();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [document.docUrl]);

  return (
    <section className="text-gray-600 body-font overflow-hidden">
      <div className="container px-5 py-24 mx-auto">
        <div className="lg:w-5/6 mx-auto flex flex-wrap">
          <div className="lg:w-1/2 w-full lg:pr-10 lg:py-6 mb-6 lg:mb-0">
            <h2 className="text-sm title-font text-gray-500 tracking-widest">{document.uploaded_time}</h2>
            <h1 className="text-gray-900 text-3xl title-font font-medium mb-4">Document Number: {document.id}</h1>
            <p className="leading-relaxed mb-4">
              <span className="mr2 fw6">Driver Comments: </span> {document.comment}
            </p>
            <div className="flex border-y border-gray-200 py-2">
              <span className="text-gray-500">Document Type</span>
              <span className="ml-auto text-gray-900">{document.document_type}</span>
            </div>
            <div className="flex border-b border-gray-200 py-2">
              <span className="text-gray-500">Status</span>
              <span className="ml-auto text-gray-900">{document.status}</span>
            </div>
            <div className="flex py-8">
              {isUpdating ? (
                <Loading>Updating status</Loading>
              ) : (
                <button
                  onClick={() => onStatusUpdate(document.status === "OPEN" ? "CLOSE" : "RESET")}
                  className="flex ml-auto text-white bg-blue-500 border-0 py-2 px-6 focus:outline-none hover:bg-blue-600 rounded"
                >
                  {document.status === "OPEN" ? 'Close' : 'Reset Status'}
                </button>
              )}
              <span className="text-secondary">
                <PaperDownload size={38} style={{marginLeft: 10}} onClick={onDownload}/>
              </span>
            </div>
          </div>
          <div className="lg:w-1/2 w-full h-[70vh]">
            {pdfLoading ? (
              <Loading type='points' />
            ) : (
              <iframe
                className="object-cover object-center rounded w-full h-full"
                src={previewUrl}
                title="Document Preview"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const SafetyDocuments = () => {
  const [state, setState] = useState({
    documentList: [],
    currentDocument: null,
    documentTypeList: [],
    isModalOpen: false,
    isUpdating: false,
    isFetching: false,
    filters: {
      documentNumber: 0,
      employeeId: "",
      documentType: "",
      dateEntered: "",
      openCheck: 0,
      closeCheck: 0
    }
  });

  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const access = useSelector(state => state.navigation.Access);
  const gridHeight = window.innerHeight - 160;

  const fetchDocumentTypeList = useCallback(async () => {
    const [ok, , json] = await secureFetch(endpoint.getDocumentTypeList, {});
    if (ok) {
      setState(prev => ({ ...prev, documentTypeList: json.documentTypeList }));
    } else {
      error("Failed to fetch document types");
    }
  }, []);

  const fetchDocumentList = useCallback(async () => {
    setState(prev => ({ ...prev, isFetching: true }));
    const { filters } = state;
    
    const [ok, , json] = await secureFetch(endpoint.fetchSafetyDocumentList, {
      clientId,
      documentNumber: filters.documentNumber || 0,
      employeeId: filters.employeeId,
      documentType: filters.documentType,
      dateEntered: filters.dateEntered,
      status: filters.openCheck === 0 && filters.closeCheck === 0 ? -1 
             : filters.openCheck === 1 ? 1 : 0
    });

    if (ok) {
      setState(prev => ({
        ...prev,
        documentList: json.safetyDocumentList,
        isFetching: false
      }));
    } else {
      error("Failed to fetch document list");
      setState(prev => ({ ...prev, isFetching: false }));
    }
  }, [clientId, state.filters]);

  const updateDocumentStatus = async (status) => {
    setState(prev => ({ ...prev, isUpdating: true }));
    
    const [ok] = await secureFetch(endpoint.updateSafetyDocumentStatus, {
      documentNumber: state.currentDocument.id,
      status: status === "CLOSE" ? 0 : 1
    });

    if (ok) {
      success("Document status updated successfully");
      setState(prev => ({ ...prev, isModalOpen: false }));
      await fetchDocumentList();
    } else {
      error("Failed to update document status");
    }
    
    setState(prev => ({ ...prev, isUpdating: false }));
  };

  const handleDownload = useCallback(() => {
    const filename = `safety_document_${Math.random().toString(36).substr(2, 5)}.jpg`;
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = state.currentDocument.docUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [state.currentDocument]);

  const handleRowDoubleClick = useCallback((row) => {
    setState(prev => ({
      ...prev,
      isModalOpen: true,
      currentDocument: row.data
    }));
  }, []);

  const handleFilterChange = useCallback((field, value) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: value }
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setState(prev => ({
      ...prev,
      filters: {
        documentNumber: 0,
        employeeId: "",
        documentType: "",
        dateEntered: "",
        openCheck: 0,
        closeCheck: 0
      }
    }));
  }, []);

  useEffect(() => {
    const init = async () => {
      await checkAccess(clientId);
      await Promise.all([
        fetchDocumentList(),
        fetchDocumentTypeList()
      ]);
    };

    init();
  }, [clientId]);

  if (!access.allow_safety_documents) {
    return (
      <div>
        <div className="flex justify-between">
          <h4 className="f3 fw4 mb1 pt1 pb3 ph1 mt2">Safety Management</h4>
        </div>
        <div className="ml2 mt3 f5">This feature is unavailable</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between p-4 border-y-2 border-dotted">
        <SubPageHeader>Safety Management</SubPageHeader>
        <div className="flex">
          <Tooltip
            css={{br: 0}}
            placement="bottom"
            content={
              <Filter
                {...state.filters}
                documentTypeList={state.documentTypeList}
                setValueToState={handleFilterChange}
                fetchSafetyDocumentList={fetchDocumentList}
                fetching={state.isFetching}
                resetFilter={resetFilters}
              />
            }
            trigger="click"
          >
            <Button css={{br: 0, ml: 10}} icon={<Filter2 set='light' />}>
              <Text size='1rem' color='currentColor' css={{pl: 30}}>
                Filter Safety Documents
              </Text>
            </Button>
          </Tooltip>
        </div>
      </div>

      {state.isFetching ? (
        <div className="flex justify-center mt6">
          <Loading size="lg">Fetching safety documents. Hold on!</Loading>
        </div>
      ) : (
        <div style={{ height: gridHeight + 10 }} className="mr2 mt2">
          {state.documentList.length === 0 ? (
            <div className="mt3 f5">No document found.</div>
          ) : (
            <div style={{ height: gridHeight }} className="ag-theme-quartz p-4">
              <AgGridReact
                columnDefs={COLUMN_DEFS}
                rowData={state.documentList}
                pagination={true}
                paginationAutoPageSize={true}
                onGridReady={params => params.api.sizeColumnsToFit()}
                onRowDoubleClicked={handleRowDoubleClick}
                getRowId={params => params.data.id}
                enableColResize
                enableSorting
                enableFilter
              />
            </div>
          )}
        </div>
      )}

      <Modal
        css={{br: 0}}
        height='95vh'
        width="90vw"
        open={state.isModalOpen}
        blur
        closeButton
        onClose={() => setState(prev => ({ ...prev, isModalOpen: false }))}
      >
        <Modal.Body>
          {state.currentDocument && (
            <DocumentPreview
              document={state.currentDocument}
              onClose={() => setState(prev => ({ ...prev, isModalOpen: false }))}
              onStatusUpdate={updateDocumentStatus}
              isUpdating={state.isUpdating}
              onDownload={handleDownload}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SafetyDocuments;
